import { parse } from "valibot";
import { rewriteAuctionMedia } from "./auction-media.ts";
import {
  auctionTextSchema,
  parseAuction,
  parseAuctionBidResult,
  parseAuctionBidSubmission,
  parseAuctionPurchaseQuote,
  parseAuctionPurchaseQuoteSelection,
  parseAuctionPurchaseReceipt,
  parseAuctionPurchaseResult,
  parseAuctionPurchaseStatus,
  parseAuctionPurchaseSubmission,
  parseAuctions,
} from "./auction-schemas.ts";
import type {
  CreateLockerverseAuctionOptions,
  LockerverseAuctionClient,
} from "./auction-types.ts";
import { deepFreeze } from "./deep-freeze.ts";
import {
  parseResourceBaseUrl,
  resolveResourceEnvironment,
} from "./environment.ts";
import { createSdkErrorReporter } from "./error-reporting.ts";
import { createTransport } from "./transport.ts";
import {
  LockerverseSdkError,
  type LockerverseSdkErrorCode,
  type LockerverseSdkOperation,
} from "./types.ts";

type RequestContext = {
  code: LockerverseSdkErrorCode;
  operation: LockerverseSdkOperation;
  message: string;
};

function auctionRequestUrl(
  baseUrl: string,
  segments: string[],
  previewItemSlug?: string
): string {
  const path =
    baseUrl +
    segments
      .map(
        (segment) => `/${encodeURIComponent(parse(auctionTextSchema, segment))}`
      )
      .join("");
  return previewItemSlug === undefined
    ? path
    : `${path}?${new URLSearchParams({
        previewItem: parse(auctionTextSchema, previewItemSlug),
      })}`;
}

export function createLockerverseAuctionClient(
  options: CreateLockerverseAuctionOptions
): LockerverseAuctionClient {
  const runtime = resolveResourceEnvironment(options, "communitySlug");
  const mediaBaseUrl =
    options.mediaBaseUrl === undefined
      ? undefined
      : parseResourceBaseUrl(
          options.mediaBaseUrl,
          runtime.environment,
          "mediaBaseUrl"
        );
  const parseAuctionResponse = <Value>(value: Value) =>
    rewriteAuctionMedia(parseAuction(value), mediaBaseUrl);
  const reportError = createSdkErrorReporter({
    communitySlug: runtime.communitySlugValue,
    environment: runtime.environment,
    getContext: () => ({}),
    onError: runtime.onError,
    resourceContextName: "lockerverse_auction",
    sentryDsn: runtime.sentryDsn,
  });
  const { requestJson } = createTransport(
    runtime.fetchImplementation,
    reportError,
    runtime.requestTimeoutMs
  );
  const baseUrl = `${runtime.apiBaseUrl}/auctions/communities/slug/${runtime.communitySlug}`;

  async function request<T, Submission, ParsedSubmission>(
    context: RequestContext,
    segments: string[],
    parser: <Value>(value: Value) => T,
    submission?: {
      value: Submission;
      parse: (value: Submission) => ParsedSubmission;
    },
    previewItemSlug?: string,
    readOnly = false
  ): Promise<T> {
    const mutatesPayment = Boolean(submission) && !readOnly;
    let url: string;
    let init: RequestInit | undefined;
    try {
      url = auctionRequestUrl(baseUrl, segments, previewItemSlug);
      if (submission) {
        init = {
          body: JSON.stringify(submission.parse(submission.value)),
          headers: { "content-type": "application/json" },
          method: "POST",
        };
      }
    } catch {
      // biome-ignore lint/style/useErrorCause: Validation details can contain personal data or confirmation tokens.
      throw reportError(
        new LockerverseSdkError({
          ...context,
          message: "Lockerverse auction input is invalid.",
          reportable: false,
        })
      );
    }
    try {
      const response = await requestJson(
        url,
        init,
        parser,
        { ...context, report: false },
        { ...context, report: false }
      );
      if (!response.ok) {
        const { status } = response.response;
        throw new LockerverseSdkError({
          ...context,
          recoveryRecommended:
            mutatesPayment && (status >= 500 || status === 408),
          reportable: status >= 500,
          status,
        });
      }
      return deepFreeze(response.value);
    } catch (error) {
      if (error instanceof LockerverseSdkError) {
        throw reportError(
          new LockerverseSdkError({
            ...context,
            recoveryRecommended:
              error.recoveryRecommended ||
              (mutatesPayment && error.status === undefined),
            reportable: error.reportable,
            ...(error.status === undefined
              ? undefined
              : { status: error.status }),
          })
        );
      }
      throw reportError(
        new LockerverseSdkError({
          ...context,
          recoveryRecommended: mutatesPayment,
          reportable: true,
        })
      );
    }
  }

  return {
    createBid: (auctionSlug, itemId, submission) =>
      request(
        {
          code: "auction_bid_failed",
          message: "Unable to submit Lockerverse auction bid.",
          operation: "create_auction_bid",
        },
        ["path", auctionSlug, "items", itemId, "bids"],
        parseAuctionBidResult,
        { parse: parseAuctionBidSubmission, value: submission }
      ),
    createPurchase: (auctionSlug, itemId, submission) =>
      request(
        {
          code: "auction_purchase_failed",
          message: "Unable to submit Lockerverse auction purchase.",
          operation: "create_auction_purchase",
        },
        ["path", auctionSlug, "items", itemId, "purchases"],
        parseAuctionPurchaseResult,
        { parse: parseAuctionPurchaseSubmission, value: submission }
      ),
    getPurchaseQuote: (auctionSlug, itemId, selection) =>
      request(
        {
          code: "auction_load_failed",
          message: "Unable to load Lockerverse auction purchase quote.",
          operation: "load_auction",
        },
        ["path", auctionSlug, "items", itemId, "purchase-quote"],
        parseAuctionPurchaseQuote,
        { parse: parseAuctionPurchaseQuoteSelection, value: selection },
        undefined,
        true
      ),
    getPurchaseReceipt: (auctionSlug, itemSlug, purchaseId) =>
      request(
        {
          code: "auction_receipt_failed",
          message: "Unable to load Lockerverse auction purchase receipt.",
          operation: "get_auction_purchase_receipt",
        },
        ["path", auctionSlug, "items", itemSlug, "purchases", purchaseId],
        parseAuctionPurchaseReceipt
      ),
    getPurchaseStatus: (auctionSlug, itemId, clientRequestId) =>
      request(
        {
          code: "auction_purchase_status_failed",
          message: "Unable to load Lockerverse auction purchase status.",
          operation: "get_auction_purchase_status",
        },
        [
          "path",
          auctionSlug,
          "items",
          itemId,
          "purchase-status",
          clientRequestId,
        ],
        parseAuctionPurchaseStatus
      ),
    list: () =>
      request(
        {
          code: "auction_load_failed",
          message: "Unable to load Lockerverse auctions.",
          operation: "list_auctions",
        },
        [],
        (value) =>
          parseAuctions(value).map((auction) =>
            rewriteAuctionMedia(auction, mediaBaseUrl)
          )
      ),
    load: (auctionSlug, loadOptions) =>
      request(
        {
          code: "auction_load_failed",
          message: "Unable to load Lockerverse auction.",
          operation: "load_auction",
        },
        ["path", auctionSlug],
        parseAuctionResponse,
        undefined,
        loadOptions?.previewItemSlug
      ),
  };
}
